const DB_NAME = "ranks-cache";
const DB_VERSION = 1;
const STORE_NAME = "apiTypePayloads";
const DEFAULT_TIMEOUT_MS = 3000;
const EXPECTED_SCHEMA_VERSION = "3.0";
const BASE_ROWS_KEY_PREFIX = "baseRows:";

let _testOpenOverride = null;

function getIndexedDB() {
  return typeof indexedDB !== "undefined" ? indexedDB : null;
}

function makeRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("IndexedDB request failed"));
  });
}

export function __setIndexedDbOpenForTest(fn) {
  _testOpenOverride = fn;
}

function openDb() {
  const idb = getIndexedDB();
  if (!idb) return Promise.reject(new Error("indexedDB unavailable"));

  if (_testOpenOverride) {
    const result = _testOpenOverride();
    if (result && typeof result.then === "function") {
      return result;
    }
    if (result && result instanceof IDBOpenDBRequest) {
      return makeRequest(result);
    }
    return Promise.reject(new Error("Test override returned invalid request"));
  }

  return new Promise((resolve, reject) => {
    const request = idb.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "typeKey" });
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      resolve(db);
    };

    request.onerror = () => reject(request.error || new Error("IndexedDB open failed"));
    request.onblocked = () => reject(new Error("IndexedDB open blocked"));
  });
}

function withTimeout(promise, timeoutMs) {
  const ms = typeof timeoutMs === "number" && timeoutMs > 0 ? timeoutMs : DEFAULT_TIMEOUT_MS;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error("IndexedDB operation timed out"));
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function isValidRecord(record, expectedTypeKey, expectedLastUpdated) {
  if (!record || typeof record !== "object") return false;
  if (record.schemaVersion !== EXPECTED_SCHEMA_VERSION) return false;
  if (record.typeKey !== expectedTypeKey) return false;
  const payload = parseRecordPayload(record);
  if (!payload || typeof payload !== "object") return false;
  if (payload.ok !== true) return false;
  if (!Array.isArray(payload.items)) return false;
  if (expectedLastUpdated != null && record.lastUpdated !== expectedLastUpdated) return false;
  return true;
}

function parseRecordPayload(record) {
  if (!record || typeof record !== "object") return null;
  if (typeof record.payloadJson === "string") {
    try {
      return JSON.parse(record.payloadJson);
    } catch {
      return null;
    }
  }
  return record.payload && typeof record.payload === "object" ? record.payload : null;
}

export async function readTypePayload(typeKey, options = {}) {
  try {
    const db = await withTimeout(openDb(), options.timeoutMs);
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.close();
      return null;
    }
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const record = await withTimeout(makeRequest(store.get(typeKey)), options.timeoutMs);
    db.close();
    if (!isValidRecord(record, typeKey, options.lastUpdated)) return null;
    return {
      ...record,
      payload: parseRecordPayload(record),
      payloadJson: undefined,
    };
  } catch {
    return null;
  }
}

export async function writeTypePayload(typeKey, envelope) {
  try {
    const db = await withTimeout(openDb(), DEFAULT_TIMEOUT_MS);
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.close();
      return false;
    }
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const payloadJson =
      typeof envelope?.payloadJson === "string" ? envelope.payloadJson : JSON.stringify(envelope?.payload || null);
    await withTimeout(
      makeRequest(
        store.put({
          ...envelope,
          typeKey,
          payload: undefined,
          payloadJson,
        }),
      ),
      DEFAULT_TIMEOUT_MS,
    );
    db.close();
    return true;
  } catch {
    return false;
  }
}

function isValidBaseRowsRecord(record, expectedCacheKey) {
  if (!record || typeof record !== "object") return false;
  if (record.typeKey !== `${BASE_ROWS_KEY_PREFIX}${expectedCacheKey}`) return false;
  if (record.kind !== "baseRows") return false;
  if (!Array.isArray(record.rows)) return false;
  return record.rows.every(
    (row) =>
      row &&
      typeof row === "object" &&
      typeof row.html === "string" &&
      typeof row.itemId === "string" &&
      typeof row.index === "number",
  );
}

export async function readBaseRowsPayload(cacheKey, options = {}) {
  try {
    const db = await withTimeout(openDb(), options.timeoutMs);
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.close();
      return null;
    }
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const record = await withTimeout(makeRequest(store.get(`${BASE_ROWS_KEY_PREFIX}${cacheKey}`)), options.timeoutMs);
    db.close();
    if (!isValidBaseRowsRecord(record, cacheKey)) return null;
    return record;
  } catch {
    return null;
  }
}

export async function writeBaseRowsPayload(cacheKey, envelope) {
  try {
    const db = await withTimeout(openDb(), DEFAULT_TIMEOUT_MS);
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.close();
      return false;
    }
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    await withTimeout(
      makeRequest(
        store.put({
          ...envelope,
          typeKey: `${BASE_ROWS_KEY_PREFIX}${cacheKey}`,
          kind: "baseRows",
          cacheKey,
        }),
      ),
      DEFAULT_TIMEOUT_MS,
    );
    db.close();
    return true;
  } catch {
    return false;
  }
}

export async function deleteTypePayload(typeKey) {
  try {
    const db = await withTimeout(openDb(), DEFAULT_TIMEOUT_MS);
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.close();
      return false;
    }
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    await withTimeout(makeRequest(store.delete(typeKey)), DEFAULT_TIMEOUT_MS);
    db.close();
    return true;
  } catch {
    return false;
  }
}

export async function clearAllTypePayloads() {
  try {
    const db = await withTimeout(openDb(), DEFAULT_TIMEOUT_MS);
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.close();
      return false;
    }
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    await withTimeout(makeRequest(store.clear()), DEFAULT_TIMEOUT_MS);
    db.close();
    return true;
  } catch {
    return false;
  }
}

function buildEngagementCacheKey(version, lastUpdate, type) {
  return `engagement:v${version}:${lastUpdate}:${type}`;
}

export async function readEngagementPayload(cacheKey) {
  try {
    const db = await withTimeout(openDb(), DEFAULT_TIMEOUT_MS);
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.close();
      return null;
    }
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const record = await withTimeout(makeRequest(store.get(cacheKey)), DEFAULT_TIMEOUT_MS);
    db.close();
    if (!record || typeof record !== "object") return null;
    if (record.typeKey !== cacheKey) return null;
    if (record.kind !== "engagement") return null;
    if (typeof record.payloadJson !== "string") return null;
    try {
      return JSON.parse(record.payloadJson);
    } catch {
      return null;
    }
  } catch {
    return null;
  }
}

export async function writeEngagementPayload(cacheKey, payload) {
  try {
    const db = await withTimeout(openDb(), DEFAULT_TIMEOUT_MS);
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.close();
      return false;
    }
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    await withTimeout(
      makeRequest(
        store.put({
          typeKey: cacheKey,
          kind: "engagement",
          payloadJson: JSON.stringify(payload),
          savedAt: Date.now(),
        }),
      ),
      DEFAULT_TIMEOUT_MS,
    );
    db.close();
    return true;
  } catch {
    return false;
  }
}

export { buildEngagementCacheKey };
